// Notification Service for Vue Admin
const API_BASE = process.env.REACT_APP_API_BASE || 'https://vue-admin-proxy.fly.dev';

class NotificationService {
  constructor() {
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator;
    this.permission = this.isSupported ? Notification.permission : 'denied';
    this.registration = null;
    this.subscription = null;
    this.pushNotificationHandlers = [];
  }

  // Check if notifications are supported
  isNotificationSupported() {
    return this.isSupported;
  }

  // Get current permission status
  getPermissionStatus() {
    return this.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) {
      throw new Error('Notifications not supported in this browser');
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      throw error;
    }
  }

  // Register service worker
  async registerServiceWorker() {
    if (!this.isSupported) {
      throw new Error('Service Worker not supported in this browser');
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', this.registration);
      
      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;
      
      return this.registration;
    } catch (error) {
      console.error('Error registering service worker:', error);
      throw error;
    }
  }

  // Subscribe to push notifications
  async subscribeToPushNotifications(userId) {
    console.log('[NotificationService] subscribeToPushNotifications called for user:', userId);
    if (!this.registration) {
      await this.registerServiceWorker();
    }

    if (this.permission !== 'granted') {
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.log('[NotificationService] Notification permission denied');
        throw new Error('Notification permission denied');
      }
    }

    try {
      // Get VAPID public key from server
      const vapidResponse = await fetch(`${API_BASE}/api/notifications/vapid-public-key`);
      const vapidData = await vapidResponse.json();
      const vapidPublicKey = vapidData.publicKey;
      console.log('[NotificationService] VAPID public key:', vapidPublicKey);

      if (!vapidPublicKey) {
        throw new Error('VAPID public key not available');
      }

      // Get push subscription
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('[NotificationService] Push subscription created:', this.subscription);

      // Send subscription to server
      await this.sendSubscriptionToServer(userId, this.subscription);

      return this.subscription;
    } catch (error) {
      console.error('[NotificationService] Error subscribing to push notifications:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribeFromPushNotifications() {
    if (this.subscription) {
      try {
        await this.subscription.unsubscribe();
        this.subscription = null;
        console.log('Unsubscribed from push notifications');
      } catch (error) {
        console.error('Error unsubscribing from push notifications:', error);
      }
    }
  }

  // Send subscription to server
  async sendSubscriptionToServer(userId, subscription) {
    console.log('[NotificationService] sendSubscriptionToServer called for user:', userId, subscription);
    try {
      const response = await fetch(`${API_BASE}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          subscription: subscription.toJSON(),
          timestamp: new Date().toISOString()
        })
      });

      if (!response.ok) {
        throw new Error(`[NotificationService] Server responded with ${response.status}`);
      }

      const result = await response.json();
      console.log('[NotificationService] Subscription sent to server:', result);
      return result;
    } catch (error) {
      console.error('[NotificationService] Error sending subscription to server:', error);
      throw error;
    }
  }

  // Show local notification (for testing)
  async showLocalNotification(title, options = {}) {
    if (!this.isSupported || this.permission !== 'granted') {
      throw new Error('Notifications not supported or permission not granted');
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'local-notification',
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      // Auto-close after 5 seconds if not requiring interaction
      if (!defaultOptions.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      return notification;
    } catch (error) {
      console.error('Error showing local notification:', error);
      throw error;
    }
  }

  // Test notification
  async testNotification() {
    try {
      // Use service worker notification for actions support
      if (this.registration) {
        await this.registration.showNotification('Test Notification', {
          body: 'This is a test notification from Vue Admin',
          icon: '/logo192.png',
          badge: '/logo192.png',
          tag: 'test-notification',
          requireInteraction: true,
          actions: [
            {
              action: 'test',
              title: 'Test Action',
              icon: '/logo192.png'
            }
          ]
        });
      } else {
        // Fallback to local notification without actions
        await this.showLocalNotification('Test Notification', {
          body: 'This is a test notification from Vue Admin',
          requireInteraction: true
        });
      }
    } catch (error) {
      console.error('Error showing test notification:', error);
      throw error;
    }
  }

  // Convert VAPID public key to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Get subscription status
  async getSubscriptionStatus() {
    if (!this.registration) {
      return { subscribed: false, reason: 'No service worker registration' };
    }

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      return {
        subscribed: !!subscription,
        subscription: subscription ? subscription.toJSON() : null
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return { subscribed: false, reason: error.message };
    }
  }

  // Initialize notification service
  async initialize(userId) {
    console.log('[NotificationService] initialize called for user:', userId);
    if (!this.isSupported) {
      console.warn('Notifications not supported in this browser');
      return false;
    }

    try {
      // Register service worker
      await this.registerServiceWorker();
      
      // Check if already subscribed
      const status = await this.getSubscriptionStatus();
      
      if (!status.subscribed) {
        // Subscribe to push notifications
        await this.subscribeToPushNotifications(userId);
      } else {
        this.subscription = await this.registration.pushManager.getSubscription();
      }

      // Set up message listener for push notifications from service worker
      this.setupMessageListener();

      console.log('[NotificationService] Notification service initialized successfully');
      return true;
    } catch (error) {
      console.error('[NotificationService] Error initializing notification service:', error);
      return false;
    }
  }

  // Set up message listener for service worker communication
  setupMessageListener() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'PUSH_NOTIFICATION') {
          console.log('Received push notification from service worker:', event.data.notification);
          this.triggerPushNotification(event.data.notification);
        }
      });
    }
  }

  // Add push notification handler
  onPushNotification(handler) {
    this.pushNotificationHandlers.push(handler);
  }

  // Remove push notification handler
  offPushNotification(handler = null) {
    if (handler) {
      this.pushNotificationHandlers = this.pushNotificationHandlers.filter(h => h !== handler);
    } else {
      this.pushNotificationHandlers = [];
    }
  }

  // Trigger push notification handlers (called by service worker)
  triggerPushNotification(notification) {
    console.log('Triggering push notification handlers:', notification);
    this.pushNotificationHandlers.forEach(handler => {
      try {
        handler(notification);
      } catch (error) {
        console.error('Error in push notification handler:', error);
      }
    });
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService; 